import * as React from 'react';
import {useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {ValidatedTextInput} from '@deviceinsight/ng-ui-components';
import type {NotificationTemplate} from '@deviceinsight/ng-ui-api-client';
import type {UiAction, UiEmailAction} from '../../../modules/ruleengine/types';
import {ACTION_TYPE_EMAIL} from '../../../modules/ruleengine/config/consts';
import DeviceAndActionIcon from '../../common/DeviceAndActionIcon';
import TranslatedOption from '../../common/TranslatedOption';

type Props = {
	action: UiEmailAction;
	index: number;
	onChange: (arg0: UiAction, arg1: number) => void;
	emailTemplates?: Array<NotificationTemplate>;
	canEdit: boolean;
	canReadNotificationTemplates: boolean;
};

const EMAIL_PATTERN =
	/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const RuleActionEmail: React.FC<Props> = ({
	action,
	index,
	onChange,
	emailTemplates,
	canEdit,
	canReadNotificationTemplates
}) => {
	const {t} = useTranslation();
	const [emailTemplateId, setEmailTemplateId] = React.useState<string | null | undefined>(undefined);
	const [receiverEmail, setReceiverEmail] = React.useState<string | null | undefined>(undefined);
	const shouldCallOnChange = React.useRef(false);
	const isInitialized = React.useRef(false);

	const buildAction = React.useCallback(
		(uuid: string): UiAction => {
			const isValid =
				emailTemplateId && receiverEmail && receiverEmail !== '' && EMAIL_PATTERN.test(receiverEmail)
					? true
					: false;
			return {
				actionType: ACTION_TYPE_EMAIL,
				receiverEmail,
				emailTemplateId,
				metadata: {
					uuid,
					isValid
				}
			};
		},
		[emailTemplateId, receiverEmail]
	);

	// Effect to call onChange when state changes and we should notify parent
	React.useEffect(() => {
		if (shouldCallOnChange.current && isInitialized.current) {
			const {
				metadata: {uuid}
			} = action;
			onChange(buildAction(uuid), index);
		}
		shouldCallOnChange.current = false;
	}, [emailTemplateId, receiverEmail, action, buildAction, onChange, index]);

	const onTemplateSelect = React.useCallback((selectedEmailTemplateId: string) => {
		shouldCallOnChange.current = true;
		setEmailTemplateId(selectedEmailTemplateId);
	}, []);

	const onEMailChange = React.useCallback((newReceiverEmail: string) => {
		shouldCallOnChange.current = true;
		setReceiverEmail(newReceiverEmail);
	}, []);

	// Update local state when action prop changes (e.g., after save)
	React.useEffect(() => {
		const {emailTemplateId: actionEmailTemplateId, receiverEmail: actionReceiverEmail} = action;

		// Only update if values are different to avoid unnecessary re-renders
		if (actionEmailTemplateId !== emailTemplateId) {
			setEmailTemplateId(actionEmailTemplateId);
		}
		if (actionReceiverEmail !== receiverEmail) {
			setReceiverEmail(actionReceiverEmail);
		}

		if (!canReadNotificationTemplates && actionEmailTemplateId !== undefined) {
			shouldCallOnChange.current = true; // Need to call onChange for this case
			setEmailTemplateId(undefined);
		}

		// Mark as initialized after first run
		if (!isInitialized.current) {
			isInitialized.current = true;
		}
	}, [action, canReadNotificationTemplates, emailTemplateId, receiverEmail]);

	const placeholder = t('ruleengine.action.email.recipient', {
		defaultValue: 'Recipient e-mail adress'
	});

	return (
		<div className="rule-action-item" data-testid="email-action">
			<DeviceAndActionIcon asset={undefined} type={ACTION_TYPE_EMAIL} />
			<div className="action-element-large">
				<ValidatedTextInput
					value={receiverEmail || ''}
					onChange={onEMailChange}
					pattern={EMAIL_PATTERN}
					invalidStyle={{
						borderColor: 'red'
					}}
					placeholder={placeholder}
					required={true}
					disabled={!canEdit}
					data-testid="email-input"
				/>
			</div>
			<div className="action-element-medium">
				<select
					onChange={({target: {value}}) => {
						onTemplateSelect(value);
					}}
					value={emailTemplateId || undefined}
					disabled={!canEdit}
					data-testid="template-selection">
					{!emailTemplateId && (
						<TranslatedOption
							trans={{
								id: `ruleengine.element.select.template`,
								defaultValue: `Please select template`
							}}
							value={undefined}
						/>
					)}
					{emailTemplates?.map((template) => (
						<option key={template.name} value={template.name}>
							{template.displayName || template.name}
						</option>
					))}
				</select>
			</div>
		</div>
	);
};

export default RuleActionEmail;
